import Layout from 'components/layout';

import { graphql, navigate } from 'gatsby';
import { Box } from 'lucid-components-react';
import React, { useEffect } from 'react';

import FooterAbTest from 'components/ab-tests/footer-swap/footer-swap';
import NavbarAbTest from 'components/ab-tests/nav-swap/nav-swap';
import NineteenHeroImage from 'components/ab-tests/nineteen-hero-image-swap/nineteen-hero-image';
import NineteenArticle from 'components/nineteen/nineteen-article/nineteen-article';
import NineteenBanner from 'components/nineteen/nineteen-banner/nineteen-banner';
import NineteenFooter from 'components/nineteen/nineteen-footer/nineteen-footer';
import NineteenHero from 'components/nineteen/nineteen-hero/nineteen-hero';
import NineteenHighlights from 'components/nineteen/nineteen-highlights/nineteen-highlights';
import NineteenInsights from 'components/nineteen/nineteen-insights/nineteen-insights';
import NineteenQuote from 'components/nineteen/nineteen-quote/nineteen-quote';
import NineteenSocialProof from 'components/nineteen/nineteen-social-proof/nineteen-social-proof';
import NineteenTextCta from 'components/nineteen/nineteen-text-cta/nineteen-text-cta';
import {
  Drupal_FieldNodeNineteenFieldNineteenParagraphs,
  Drupal_NodeNineteenHomepage,
  Drupal_Paragraph
} from 'generated/graphql';
import { useMenuFooter, useMenuMainNavLinks } from 'hooks/use-menu';
import { backgroundGrayPattern } from 'theme/shared';
import { TemplateProps } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';

interface NavigatorLanguage extends Navigator {
  userLanguage?: string;
}

const NineteenHomepage = ({ data, pageContext, location }: TemplateProps) => {
  const { langPrefix, pageType, nid, hideSsr } = pageContext;

  const { drupal } = data;
  const node = drupal.nodeById as Drupal_NodeNineteenHomepage;
  const { entityMetatags, entityTranslations } = getEntityFields(node);

  const componentMapping: Record<string, any> = {
    nineteen_article: NineteenArticle,
    nineteen_banner: NineteenBanner,
    nineteen_footer: NineteenFooter,
    nineteen_hero: NineteenHero,
    nineteen_hero_image: NineteenHeroImage,
    nineteen_highlights: NineteenHighlights,
    nineteen_insights: NineteenInsights,
    nineteen_quote: NineteenQuote,
    nineteen_social_proof: NineteenSocialProof,
    nineteen_text_cta: NineteenTextCta
  };

  const nineteenParagraphs = node.fieldNineteenParagraphs as Drupal_FieldNodeNineteenFieldNineteenParagraphs[];

  const navbarMenu = useMenuMainNavLinks(langPrefix);
  const footerMenu = useMenuFooter(langPrefix);

  // I18n Redirect
  useEffect(() => {
    if (!window.document.referrer && langPrefix === 'en') {
      const extendedNavigator = navigator as NavigatorLanguage;
      const preferredLanguage = extendedNavigator.languages
        ? extendedNavigator.languages[0]
        : extendedNavigator.language || extendedNavigator.userLanguage;
      if (preferredLanguage) {
        const normalizedLanguage = preferredLanguage.toLowerCase();
        const i18nRoute = entityTranslations.find(({ entityLanguage }) => {
          const routeId = entityLanguage!.id;
          return (
            routeId === normalizedLanguage ||
            routeId!.startsWith(normalizedLanguage.substring(0, 2))
          );
        });
        if (i18nRoute && i18nRoute.entityLanguage!.id !== langPrefix) {
          navigate(i18nRoute.entityUrl!.path!, { replace: true });
        }
      }
    }
  }, [entityTranslations, langPrefix]);

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
    >
      {node.fieldHasNavbar && (
        <NavbarAbTest menu={navbarMenu} scrollDistance={1} />
      )}

      <Box as="main" css={{ ...backgroundGrayPattern }}>
        {nineteenParagraphs.map(({ entity }) => {
          const { entityBundle, entityId } = entity as Drupal_Paragraph;
          const NineteenComponent = componentMapping[entityBundle as string];

          return NineteenComponent ? (
            <NineteenComponent key={entityId} entity={entity} />
          ) : null;
        })}
      </Box>

      {node.fieldHasFooter && (
        <FooterAbTest
          padFooter
          entityTranslations={entityTranslations}
          menu={footerMenu}
        />
      )}
    </Layout>
  );
};

export default NineteenHomepage;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeNineteenHomepage {
          ...entityFields
          fieldHasNavbar
          fieldHasFooter
          fieldNineteenParagraphs {
            entity {
              entityId
              entityBundle
              ...nineteenArticle
              ...nineteenBanner
              ...nineteenFooter
              ...nineteenHero
              ...nineteenHeroImage
              ...nineteenHighlights
              ...nineteenInsights
              ...nineteenSocialProof
              ...nineteenQuote
              ...nineteenTextCta
            }
          }
        }
      }
    }
  }
`;
